/**
 * Sprint V101.d · Admin-Cockpit-Layout (Sa 20.09.2026, Andreas).
 *
 * Gemeinsamer Rahmen für alle /admin/*-Routen mit permanenter Sidebar.
 * - Reused DashboardHeader/Footer (konsistentes Auth + Logout-Erlebnis)
 * - Admin-Guard: kein Admin → Redirect nach /cockpit
 * - Sidebar mit 5 Bereichen: Overview / Users / Webinar / Feedback / Analytics
 * - Roter Akzent-Streifen oben („du bist im Admin-Bereich")
 * - Sidebar-Footer: shared Admin-Token (per AdminTokenContext, localStorage)
 */
import React, { useEffect, useMemo, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  ShieldCheck,
  LayoutGrid,
  Users,
  Calendar,
  MessageSquare,
  BarChart3,
  Megaphone,
  FlaskConical,
  Eye,
  EyeOff,
  Loader2,
  Flame,
  Scale,
} from "lucide-react";
import DashboardHeader from "@/components/DashboardHeader";
import Footer from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import { useAdminToken } from "@/context/AdminTokenContext";

const NAV_ITEMS = [
  { to: "/admin", label: "Overview", Icon: LayoutGrid, end: true },
  { to: "/admin/users", label: "Users", Icon: Users },
  { to: "/admin/webinar", label: "Webinar", Icon: Calendar },
  { to: "/admin/marketing", label: "Marketing", Icon: Megaphone },
  { to: "/admin/feedback", label: "Feedback", Icon: MessageSquare },
  { to: "/admin/landing-analytics", label: "Analytics", Icon: BarChart3 },
  { to: "/admin/kohorten", label: "Kohorten", Icon: Users },
  { to: "/admin/entscheidungen", label: "Entscheidungen", Icon: Scale },
  { to: "/admin/challenge", label: "Challenge", Icon: Flame },
  { to: "/admin/kt400", label: "KT400-Seiten", Icon: Flame },
  { to: "/admin/questionset-ab", label: "Fragen-A/B", Icon: FlaskConical },
];

export default function AdminLayout({ children }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { token, setToken } = useAdminToken();
  const [showToken, setShowToken] = useState(false);

  const authChecking = user === null;
  const isAdmin = useMemo(
    () => !!user && typeof user === "object" && user.role === "admin",
    [user],
  );

  // Kein-Admin-Guard: Sobald Auth geladen ist und User kein Admin, raus.
  useEffect(() => {
    if (authChecking) return;
    if (user === false) {
      navigate("/login?next=/admin", { replace: true });
      return;
    }
    if (!isAdmin) {
      navigate("/cockpit", { replace: true });
    }
  }, [authChecking, user, isAdmin, navigate]);

  if (authChecking) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-[#F3651D] animate-spin" />
      </div>
    );
  }
  if (!isAdmin) return null; // Redirect läuft im Effect

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex flex-col" data-testid="admin-layout">
      <DashboardHeader
        firstName={user.first_name}
        statusLabel={user.account_status_label}
        statusColor={user.account_status_color}
        licenseTier={user.license_tier}
        licenseStatus={user.license_status}
        licenseData={user}
      />
      {/* Roter Admin-Kicker */}
      <div className="bg-red-600 dark:bg-red-700 text-white text-xs sm:text-sm font-bold" data-testid="admin-kicker">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-1.5 flex items-center gap-2">
          <ShieldCheck className="w-3.5 h-3.5" />
          <span className="tracking-wide">ADMIN-COCKPIT · nur du siehst diesen Bereich</span>
        </div>
      </div>

      {/* Mobile Nav (horizontal, only <lg) */}
      <nav
        className="lg:hidden flex overflow-x-auto gap-1 px-3 py-2 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800"
        data-testid="admin-nav-mobile"
      >
        {NAV_ITEMS.map(({ to, label, Icon, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            className={({ isActive }) =>
              `flex-shrink-0 inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold ${
                isActive
                  ? "bg-[#F3651D]/10 text-[#F3651D]"
                  : "text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
              }`
            }
          >
            <Icon className="w-3.5 h-3.5" />
            {label}
          </NavLink>
        ))}
      </nav>

      <div className="flex flex-1">
        {/* Sidebar (only ≥lg) */}
        <aside
          className="hidden lg:flex w-60 flex-shrink-0 border-r border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 flex-col"
          data-testid="admin-sidebar"
        >
          <nav className="p-3 space-y-1">
            {NAV_ITEMS.map(({ to, label, Icon, end }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                data-testid={`admin-nav-${label.toLowerCase()}`}
                className={({ isActive }) =>
                  `flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold transition-colors ${
                    isActive
                      ? "bg-[#F3651D]/10 text-[#F3651D]"
                      : "text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
                  }`
                }
              >
                <Icon className="w-4 h-4" />
                {label}
              </NavLink>
            ))}
          </nav>

          {/* Token-Footer */}
          <div className="mt-auto p-3 border-t border-slate-200 dark:border-slate-800">
            <label className="block text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">
              Admin-Token
            </label>
            <div className="relative">
              <input
                data-testid="admin-shared-token"
                type={showToken ? "text" : "password"}
                value={token}
                onChange={(e) => setToken(e.target.value)}
                placeholder="X-Admin-Token"
                className="w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 px-2.5 py-1.5 text-xs text-slate-900 dark:text-white font-mono pr-8"
              />
              <button
                type="button"
                onClick={() => setShowToken((v) => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                aria-label="Token anzeigen/verbergen"
              >
                {showToken ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
              </button>
            </div>
            <p className="mt-1.5 text-[10px] text-slate-500 leading-relaxed">
              Wird lokal gespeichert und von allen Admin-Panels genutzt.
            </p>
          </div>
        </aside>

        <main className="flex-1 min-w-0">
          <div className="max-w-6xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-6">
            {children}
          </div>
        </main>
      </div>
      <Footer />
    </div>
  );
}
